import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import type { RespostaToken } from "../../../shared/tipos";
import { aplicarNovoToken, api, apiPublica } from "../api/cliente";
import { useAuth } from "../auth/AuthProvider";
import { useAvisos } from "../components/Avisos";
import { Rodape, Topbar } from "../components/Layout";
import { Carregando } from "../components/ui";

function destinoSeguro(next: string | null): string {
  // Evita redirecionamento aberto: só caminhos internos.
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
}

function MolduraPublica({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Topbar />
      <div className="container" style={{ maxWidth: 420, marginTop: 40 }}>
        <div className="card">{children}</div>
      </div>
      <Rodape />
    </>
  );
}

export function Login() {
  const { usuario, carregando, entrar } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { avisarErro } = useAvisos();
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [enviando, setEnviando] = useState(false);

  if (carregando) return <Carregando />;
  if (usuario) return <Navigate to={destinoSeguro(params.get("next"))} replace />;

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    setEnviando(true);
    try {
      await entrar(email.trim().toLowerCase(), senha);
      navigate(destinoSeguro(params.get("next")), { replace: true });
    } catch (erro) {
      avisarErro(erro);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <MolduraPublica>
      <h1 style={{ marginTop: 0 }}>Entrar</h1>
      <form onSubmit={enviar}>
        <div className="form-row form-col">
          <label>
            E-mail
            <input type="email" name="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label>
            Senha
            <input type="password" name="senha" required autoComplete="current-password" value={senha} onChange={(e) => setSenha(e.target.value)} />
          </label>
        </div>
        <button type="submit" className="btn-orange" style={{ width: "100%" }} disabled={enviando}>
          Entrar
        </button>
      </form>
      <p className="note" style={{ marginTop: 14 }}>
        <Link to="/esqueci-senha">Esqueceu a senha?</Link>
      </p>
      <p className="note">
        Não tem conta? <Link to="/registrar">Criar conta</Link> — o acesso é liberado depois da aprovação do administrador.
      </p>
    </MolduraPublica>
  );
}

export function Registrar() {
  const navigate = useNavigate();
  const { avisar, avisarErro } = useAvisos();
  const [dados, setDados] = useState({ nome: "", email: "", senha: "" });
  const [enviando, setEnviando] = useState(false);

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    setEnviando(true);
    try {
      const resposta = await apiPublica<{ mensagem: string }>("/api/usuarios/registro", dados);
      avisar(resposta.mensagem);
      navigate("/login");
    } catch (erro) {
      avisarErro(erro);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <MolduraPublica>
      <h1 style={{ marginTop: 0 }}>Criar conta</h1>
      <p className="note">
        Depois de criar a conta, aguarde a aprovação do administrador para entrar. Ao ser aprovada, a conta começa com acesso somente leitura (consultar tabela, ver orçamentos); a edição é liberada pelo administrador.
      </p>
      <form onSubmit={enviar}>
        <div className="form-row form-col">
          <label>
            Nome
            <input type="text" required autoComplete="name" value={dados.nome} onChange={(e) => setDados({ ...dados, nome: e.target.value })} />
          </label>
          <label>
            E-mail
            <input type="email" required autoComplete="email" value={dados.email} onChange={(e) => setDados({ ...dados, email: e.target.value })} />
          </label>
          <label>
            Senha (mín. 6 caracteres)
            <input
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={dados.senha}
              onChange={(e) => setDados({ ...dados, senha: e.target.value })}
            />
          </label>
        </div>
        <button type="submit" className="btn-orange" style={{ width: "100%" }} disabled={enviando}>
          Criar conta
        </button>
      </form>
      <p className="note" style={{ marginTop: 14 }}>
        Já tem conta? <Link to="/login">Entrar</Link>
      </p>
    </MolduraPublica>
  );
}

export function EsqueciSenha() {
  const navigate = useNavigate();
  const { avisar, avisarErro } = useAvisos();
  const [email, setEmail] = useState("");
  const [enviando, setEnviando] = useState(false);

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    setEnviando(true);
    try {
      const resposta = await apiPublica<{ mensagem: string }>("/api/usuarios/esqueci-senha", { email: email.trim().toLowerCase() });
      avisar(resposta.mensagem);
      navigate("/login");
    } catch (erro) {
      avisarErro(erro);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <MolduraPublica>
      <h1 style={{ marginTop: 0 }}>Esqueci a senha</h1>
      <p className="note">
        Informe o e-mail da sua conta. O administrador recebe o pedido, gera uma senha temporária e te passa por
        telefone/WhatsApp. Depois de entrar, troque em "Trocar senha".
      </p>
      <form onSubmit={enviar}>
        <div className="form-row form-col">
          <label>
            E-mail
            <input type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
        </div>
        <button type="submit" className="btn-orange" style={{ width: "100%" }} disabled={enviando}>
          Pedir nova senha
        </button>
      </form>
      <p className="note" style={{ marginTop: 14 }}>
        Lembrou? <Link to="/login">Entrar</Link>
      </p>
    </MolduraPublica>
  );
}

export function ContaSenha() {
  const navigate = useNavigate();
  const { avisar, avisarErro } = useAvisos();
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [enviando, setEnviando] = useState(false);

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    setEnviando(true);
    try {
      const tokens = await api<RespostaToken>("/api/me/senha", { method: "PUT", body: { senha_atual: atual, senha_nova: nova } });
      aplicarNovoToken(tokens);
      avisar("Senha atualizada.");
      navigate("/");
    } catch (erro) {
      avisarErro(erro);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <h1>Trocar minha senha</h1>
      <div className="card" style={{ maxWidth: 420 }}>
        <form onSubmit={enviar}>
          <div className="form-row form-col">
            <label>
              Senha atual
              <input type="password" required autoComplete="current-password" value={atual} onChange={(e) => setAtual(e.target.value)} />
            </label>
            <label>
              Nova senha (mín. 6 caracteres)
              <input type="password" required minLength={6} autoComplete="new-password" value={nova} onChange={(e) => setNova(e.target.value)} />
            </label>
          </div>
          <button type="submit" className="btn-orange" disabled={enviando}>
            Salvar
          </button>
        </form>
        <p className="note" style={{ marginTop: 10 }}>Por segurança, as sessões abertas em outros dispositivos serão encerradas.</p>
      </div>
    </>
  );
}
